import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { getSpaceContent, deleteContent } from '../lib/api'
import { cn, formatDate, contentTypeConfig } from '../lib/utils'
import {
  FolderOpen,
  ChevronRight,
  ArrowLeft,
  Plus,
  MoreVertical,
  Trash2,
  Edit,
  Loader2,
  FileText,
  Globe,
  Image,
  Mic,
  BookOpen,
  File,
  Grid,
  List,
} from 'lucide-react'

const iconMap = {
  note: FileText,
  bookmark: Globe,
  image: Image,
  pdf: FileText,
  docx: FileText,
  doc: FileText,
  xlsx: FileText,
  xls: FileText,
  pptx: FileText,
  ppt: FileText,
  txt: FileText,
  epub: BookOpen,
  transcript: Mic,
  unknown: File,
}

function ContentIcon({ type, size = 'md' }) {
  const Icon = iconMap[type] || File
  const config = contentTypeConfig[type] || contentTypeConfig.unknown
  const sizeClasses = size === 'lg' ? 'p-3' : 'p-2'
  const iconSize = size === 'lg' ? 'w-6 h-6' : 'w-4 h-4'
  
  return (
    <div
      className={cn("rounded-xl", sizeClasses)}
      style={{ backgroundColor: `${config.color}20` }}
    >
      <Icon className={iconSize} style={{ color: config.color }} />
    </div>
  )
}

function ContentGridItem({ content, onClick, onDelete }) {
  const config = contentTypeConfig[content.type] || contentTypeConfig.unknown
  const [showMenu, setShowMenu] = useState(false)

  return (
    <div
      className="group relative flex flex-col p-4 rounded-xl bg-card border border-border hover:border-primary/50 hover:shadow-lg hover:shadow-primary/5 transition-all cursor-pointer"
      onClick={onClick}
    >
      <div className="flex items-start justify-between mb-3">
        <ContentIcon type={content.type} size="lg" />
        <button
          onClick={(e) => {
            e.stopPropagation()
            setShowMenu(!showMenu)
          }}
          className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg hover:bg-accent transition-all"
        >
          <MoreVertical className="w-4 h-4" />
        </button>
        {showMenu && (
          <div className="absolute right-4 top-12 z-10 bg-card border border-border rounded-xl shadow-lg py-1 min-w-[120px]">
            <button
              onClick={(e) => {
                e.stopPropagation()
                setShowMenu(false)
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-accent"
            >
              <Edit className="w-4 h-4" />
              Edit
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation()
                onDelete()
                setShowMenu(false)
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-destructive hover:bg-destructive/10"
            >
              <Trash2 className="w-4 h-4" />
              Delete
            </button>
          </div>
        )}
      </div>
      <h3 className="font-medium line-clamp-2 mb-2 group-hover:text-primary transition-colors">
        {content.title}
      </h3>
      <div className="mt-auto flex items-center gap-2">
        <span
          className="text-xs px-2 py-0.5 rounded-full"
          style={{ backgroundColor: `${config.color}20`, color: config.color }}
        >
          {config.label}
        </span>
        <span className="text-xs text-muted-foreground">
          {formatDate(content.updated_at || content.updatedAt)}
        </span>
      </div>
    </div>
  )
}

function SpaceItem({ space, onClick }) {
  return (
    <button
      onClick={onClick}
      className="group flex items-center gap-3 p-4 rounded-xl bg-card border border-border hover:border-primary/50 hover:shadow-lg transition-all text-left w-full"
    >
      <div className="p-2.5 rounded-xl bg-primary/10 group-hover:bg-primary/20 transition-colors">
        <FolderOpen className="w-5 h-5 text-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-medium truncate group-hover:text-primary transition-colors">
          {space.name}
        </h3>
        <p className="text-xs text-muted-foreground">
          {space.contents?.length || 0} items
        </p>
      </div>
      <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
    </button>
  )
}

export default function SpacePage() {
  const { spaceId } = useParams()
  const navigate = useNavigate()
  const [data, setData] = useState({ spaces: [], contents: [], parentId: null })
  const [loading, setLoading] = useState(true)
  const [viewMode, setViewMode] = useState('grid')
  const [spaceName, setSpaceName] = useState('')

  useEffect(() => {
    loadSpaceContent()
  }, [spaceId])

  const loadSpaceContent = async () => {
    setLoading(true)
    try {
      const res = await getSpaceContent(spaceId)
      setData({
        spaces: res.spaces || [],
        contents: res.contents || [],
        parentId: res.parentId,
      })
    } catch (err) {
      console.error('Failed to load space:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteContent = async (contentId) => {
    if (!confirm('Are you sure you want to delete this content?')) return
    try {
      await deleteContent(contentId)
      setData(prev => ({
        ...prev,
        contents: prev.contents.filter(c => (c.content_id || c.id) !== contentId),
      }))
    } catch (err) {
      console.error('Failed to delete content:', err)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="p-6 max-w-6xl mx-auto animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => data.parentId ? navigate(`/spaces/${data.parentId}`) : navigate('/')}
            className="p-2 rounded-lg hover:bg-accent transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
              <Link to="/" className="hover:text-foreground">Home</Link>
              <ChevronRight className="w-3 h-3" />
              <span>Space</span>
            </div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <FolderOpen className="w-6 h-6 text-primary" />
              {spaceName || `Space ${spaceId}`}
            </h1>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center bg-card border border-border rounded-lg p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={cn(
                "p-1.5 rounded",
                viewMode === 'grid' ? "bg-accent" : "hover:bg-accent/50"
              )}
            >
              <Grid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={cn(
                "p-1.5 rounded",
                viewMode === 'list' ? "bg-accent" : "hover:bg-accent/50"
              )}
            >
              <List className="w-4 h-4" />
            </button>
          </div>
          <button className="flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 transition-colors shadow-lg shadow-primary/25">
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Add</span>
          </button>
        </div>
      </div>

      {/* Sub-spaces */}
      {data.spaces.length > 0 && (
        <section className="mb-8">
          <h2 className="text-sm font-medium text-muted-foreground mb-3">Folders</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.spaces.map((space) => (
              <SpaceItem
                key={space.id}
                space={space}
                onClick={() => navigate(`/spaces/${space.id}`)}
              />
            ))}
          </div>
        </section>
      )}

      {/* Contents */}
      <section>
        {data.contents.length > 0 && (
          <h2 className="text-sm font-medium text-muted-foreground mb-3">
            Contents ({data.contents.length})
          </h2>
        )}
        {data.contents.length === 0 && data.spaces.length === 0 ? (
          <div className="p-12 rounded-xl bg-card border border-dashed border-border text-center">
            <FolderOpen className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">This space is empty</h3>
            <p className="text-muted-foreground mb-4">
              Add notes, bookmarks, or documents to get started
            </p>
            <button className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-primary-foreground">
              <Plus className="w-4 h-4" />
              Add Content
            </button>
          </div>
        ) : (
          <div className={cn(
            viewMode === 'grid'
              ? "grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
              : "space-y-2"
          )}>
            {data.contents.map((content) => (
              <ContentGridItem
                key={content.content_id || content.id}
                content={content}
                onClick={() => navigate(`/content/${content.content_id || content.id}`)}
                onDelete={() => handleDeleteContent(content.content_id || content.id)}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  )
}
